import * as Popover from "@radix-ui/react-popover";
import * as Select from "@radix-ui/react-select";
import { AlertCircle, Check, ChevronDown, Loader2, Package, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchProducto, type Producto, resolverTarifa } from "../../lib/consola-api";
import { pesos } from "../../lib/formato";
import { useDatos } from "../../lib/useDatos";
import {
  consultaDeTarifa,
  type EstadoDeTarifa,
  iniciarConsultaDeTarifa,
  type PrecioResuelto,
  subtotalRenglon,
} from "./renglones";

/**
 * Un ítem de la consulta, porteado de `BudgetItemRow` de `imprenta`.
 *
 * Se edita **en su lugar**: el ítem se agrega vacío y se completa ahí mismo, con
 * el precio resolviéndose solo a medida que se elige. No es un formulario que
 * produce una fila de tabla — la fila **es** el formulario, que es lo que hace
 * que corregir una cantidad no obligue a rehacer el renglón.
 */

/** Lo que la pantalla mantiene de cada ítem mientras se carga. */
export interface ItemEnCarga {
  clave: string;
  productoId: string | null;
  productoNombre: string;
  tamanoId: string | null;
  baseId: string | null;
  cantidad: number;
  /** Lo último que resolvió la tarifa. `null` = no hay vigente. */
  precioResuelto: PrecioResuelto | null;
}

export function itemVacio(): ItemEnCarga {
  return {
    clave: crypto.randomUUID(),
    productoId: null,
    productoNombre: "",
    tamanoId: null,
    baseId: null,
    cantidad: 1,
    precioResuelto: null,
  };
}

/**
 * Un desplegable con **texto secundario en gris** dentro de cada opción.
 *
 * Es un select de Radix y no uno nativo por una razón concreta: el navegador
 * **no deja estilar parte del texto de un `<option>`**. Las medidas del tamaño
 * —`(80x60mm)`— van en un tono más claro que el nombre, como en `imprenta`, y
 * eso con un `<select>` nativo no se puede hacer.
 */
function SelectDeItem({
  id,
  etiqueta,
  valor,
  alCambiar,
  opciones,
  deshabilitado,
}: {
  id: string;
  etiqueta: string;
  valor: string | null;
  alCambiar: (valor: string | null) => void;
  opciones: { id: string; nombre: string; detalle?: string | null }[];
  deshabilitado: boolean;
}) {
  return (
    <>
      <span className="mb-1 block font-medium text-neutral-700 text-sm">{etiqueta}</span>
      <Select.Root
        value={valor ?? "none"}
        onValueChange={(v) => alCambiar(v === "none" ? null : v)}
        disabled={deshabilitado}
      >
        <Select.Trigger
          id={id}
          aria-label={etiqueta}
          className="flex w-full items-center justify-between rounded-md border border-neutral-300 bg-white px-3 py-2 text-left text-neutral-900 text-sm hover:bg-neutral-50 disabled:opacity-50"
        >
          <Select.Value placeholder="Seleccionar..." />
          <Select.Icon>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            sideOffset={4}
            className="z-50 max-h-[300px] overflow-y-auto rounded-md border border-neutral-200 bg-white shadow-md"
          >
            <Select.Viewport className="p-1">
              <OpcionDeItem valor="none" nombre="Sin especificar" />
              {opciones.map((o) => (
                <OpcionDeItem
                  key={o.id}
                  valor={o.id}
                  nombre={o.nombre}
                  detalle={o.detalle ?? null}
                />
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </>
  );
}

function OpcionDeItem({
  valor,
  nombre,
  detalle,
}: {
  valor: string;
  nombre: string;
  detalle?: string | null;
}) {
  return (
    <Select.Item
      value={valor}
      className="cursor-pointer rounded px-2 py-1.5 text-neutral-900 text-sm outline-none data-[highlighted]:bg-neutral-100"
    >
      <Select.ItemText>
        {nombre}
        {detalle ? <span className="ml-1 text-neutral-500">({detalle})</span> : null}
      </Select.ItemText>
    </Select.Item>
  );
}

/**
 * Las medidas del tamaño, o `null` si el producto no las tiene cargadas.
 *
 * Vienen como `numeric` de Postgres, o sea `"80.00"`. Se muestran sin los ceros
 * de la escala —`80x60mm`, no `80.00x60.00mm`— que es como se leen en
 * `imprenta` y como las diría cualquiera.
 */
function medidas(anchoMm: string | null, altoMm: string | null): string | null {
  if (!anchoMm || !altoMm) return null;
  const limpio = (v: string) => String(Number(v));
  return `${limpio(anchoMm)}x${limpio(altoMm)}mm`;
}

export function RenglonConsulta({
  item,
  productos,
  clienteId,
  clienteEsVip,
  alCambiar,
  alPrecio,
  alQuitar,
  puedeQuitar,
  deshabilitado,
  conError,
}: {
  item: ItemEnCarga;
  productos: Producto[];
  clienteId: string;
  clienteEsVip: boolean;
  alCambiar: (item: ItemEnCarga) => void;
  /**
   * Avisa el precio resuelto, identificando el ítem por su clave.
   *
   * La clave viaja como argumento —y no capturada en un closure— para que el
   * padre pueda pasar UNA función estable: el efecto que dispara la consulta
   * depende de ella, y una función nueva por render consultaría en bucle.
   */
  alPrecio: (clave: string, precio: PrecioResuelto | null) => void;
  alQuitar: () => void;
  puedeQuitar: boolean;
  deshabilitado: boolean;
  /** El intento de guardar se frenó por este ítem: se marca y recibe el foco. */
  conError: boolean;
}) {
  const [buscadorAbierto, setBuscadorAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [tarifa, setTarifa] = useState<EstadoDeTarifa | null>(null);
  const productoRef = useRef<HTMLButtonElement>(null);
  const cantidadRef = useRef<HTMLInputElement>(null);

  /**
   * El foco va al PRIMER campo que falta, no siempre al mismo.
   *
   * Mandarlo al selector de producto cuando lo que falta es la cantidad hace
   * que la persona vuelva a mirar un campo que ya completó. Y el `scrollIntoView`
   * es lo que hace útil el aviso: el ítem incompleto puede estar fuera de la
   * pantalla, y el mensaje de arriba no dice cuál es.
   */
  useEffect(() => {
    if (!conError) return;
    const destino = item.productoId === null ? productoRef.current : cantidadRef.current;
    destino?.scrollIntoView({ block: "center", behavior: "smooth" });
    destino?.focus();
  }, [conError, item.productoId]);

  // Tamaños y bases del producto elegido. `imprenta` los pide igual: son
  // dependientes del producto y no se pueden precargar todos.
  const detalle = useDatos(
    () => (item.productoId ? fetchProducto(item.productoId) : Promise.resolve(null)),
    item.productoId ?? "",
  );

  const productoElegido = productos.find((p) => p.id === item.productoId) ?? null;

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return productos;
    return productos.filter((p) => p.nombre.toLowerCase().includes(texto));
  }, [productos, busqueda]);

  // El precio, en vivo. La clave estabiliza el efecto: sin ella, el objeto de
  // consulta es nuevo en cada render y volvería a pedir con cada tecla ajena.
  const consulta = consultaDeTarifa(
    { tamanoId: item.tamanoId ?? "", baseId: item.baseId ?? "", cantidad: String(item.cantidad) },
    clienteId,
  );
  const clave = consulta ? JSON.stringify(consulta) : "";

  useEffect(() => {
    if (!clave) {
      setTarifa(null);
      alPrecio(item.clave, null);
      return;
    }
    return iniciarConsultaDeTarifa(JSON.parse(clave), {
      buscar: (c, signal) => resolverTarifa(c, signal),
      alCambiar: (estado) => {
        setTarifa(estado);
        // El precio se sube al ítem EN EL MISMO callback, y no con un efecto que
        // mire el estado local: sincronizar hacia arriba con `useEffect` obliga
        // a comparar contra el valor anterior para no ciclar, y esa comparación
        // es la que se rompe cuando el objeto cambia de identidad.
        if (estado.estado === "encontrado") alPrecio(item.clave, estado.precio);
        else if (estado.estado !== "buscando") alPrecio(item.clave, null);
      },
    });
  }, [clave, alPrecio, item.clave]);

  const subtotal = subtotalRenglon(item);
  const hayProducto = item.productoId !== null;

  return (
    <div
      className={`relative space-y-4 rounded-lg border p-4 ${
        conError ? "border-red-400 bg-red-50/40" : "border-neutral-200 bg-neutral-50/50"
      }`}
    >
      <button
        type="button"
        onClick={alQuitar}
        disabled={!puedeQuitar || deshabilitado}
        aria-label="Quitar item"
        className="absolute top-2 right-2 rounded-md p-2 text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <Trash2 className="h-4 w-4" />
      </button>

      {/* ── Producto ─────────────────────────────────────────────────────── */}
      <div className="pr-10">
        <span className="mb-1 block font-medium text-neutral-700 text-sm">Producto *</span>
        <div className="relative">
          <Popover.Root open={buscadorAbierto} onOpenChange={setBuscadorAbierto}>
            <Popover.Trigger asChild>
              <button
                type="button"
                ref={productoRef}
                role="combobox"
                aria-expanded={buscadorAbierto}
                aria-label="Producto"
                aria-invalid={conError && item.productoId === null}
                disabled={deshabilitado}
                className="flex w-full items-center justify-between rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm hover:bg-neutral-50"
              >
                {productoElegido ? (
                  <span className="flex items-center">
                    <Package className="mr-2 h-4 w-4 text-brand-600" />
                    {productoElegido.nombre}
                  </span>
                ) : (
                  <span className="text-neutral-500">Seleccionar producto...</span>
                )}
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                align="start"
                sideOffset={4}
                className="z-50 w-[400px] rounded-md border border-neutral-200 bg-white p-0 shadow-md"
              >
                <div className="flex items-center border-neutral-200 border-b px-3">
                  <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <input
                    placeholder="Buscar producto..."
                    aria-label="Buscar producto"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-neutral-500"
                  />
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                  {filtrados.length === 0 ? (
                    <div className="py-6 text-center text-neutral-500 text-sm">
                      No se encontraron productos
                    </div>
                  ) : (
                    filtrados.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          alCambiar({
                            ...item,
                            productoId: p.id,
                            productoNombre: p.nombre,
                            tamanoId: null,
                            baseId: null,
                            precioResuelto: null,
                          });
                          setBuscadorAbierto(false);
                          setBusqueda("");
                        }}
                        className={`flex w-full items-center px-3 py-2 text-left text-sm hover:bg-neutral-100 ${
                          p.id === item.productoId ? "bg-neutral-100" : ""
                        }`}
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${p.id === item.productoId ? "opacity-100" : "opacity-0"}`}
                        />
                        <span className="flex-1">
                          <span className="block font-medium">{p.nombre}</span>
                          {/* Cuántas combinaciones tiene armadas: dice si el
                              producto está listo para cotizar o le falta
                              configuración, y es lo que se necesita saber
                              ANTES de elegirlo. */}
                          <span className="block text-neutral-500 text-xs">
                            {p.tamanos} tamanos - {p.bases} bases
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </div>
      </div>

      {/* ── Tamaño, base y cantidad: solo con producto elegido ───────────── */}
      {hayProducto ? (
        <div className="grid gap-4 sm:grid-cols-12">
          <div className="sm:col-span-4">
            <SelectDeItem
              id={`tamano-${item.clave}`}
              etiqueta="Tamano Logo"
              valor={item.tamanoId}
              deshabilitado={deshabilitado}
              alCambiar={(v) => alCambiar({ ...item, tamanoId: v })}
              opciones={(detalle.datos?.tamanos ?? []).map((t) => ({
                id: t.id,
                nombre: t.nombre,
                detalle: medidas(t.anchoMm, t.altoMm),
              }))}
            />
          </div>

          <div className="sm:col-span-4">
            {/* Mismo control que el tamaño aunque no tenga texto secundario:
                dos desplegables lado a lado con distinto comportamiento —uno
                nativo y otro no— se nota al usarlos. */}
            <SelectDeItem
              id={`base-${item.clave}`}
              etiqueta="Tipo Base"
              valor={item.baseId}
              deshabilitado={deshabilitado}
              alCambiar={(v) => alCambiar({ ...item, baseId: v })}
              opciones={(detalle.datos?.bases ?? []).map((b) => ({ id: b.id, nombre: b.nombre }))}
            />
          </div>

          <div className="sm:col-span-4">
            <label
              htmlFor={`cantidad-${item.clave}`}
              className="mb-1 block font-medium text-neutral-700 text-sm"
            >
              Cantidad *
            </label>
            <input
              id={`cantidad-${item.clave}`}
              ref={cantidadRef}
              type="number"
              min={1}
              aria-invalid={conError && item.productoId !== null}
              value={item.cantidad}
              disabled={deshabilitado}
              onChange={(e) => alCambiar({ ...item, cantidad: e.target.valueAsNumber || 1 })}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-neutral-900 text-sm"
            />
          </div>
        </div>
      ) : null}

      {/* ── Precio y subtotal ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between border-neutral-200 border-t pt-2">
        {hayProducto ? (
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className="mr-2 text-neutral-500 text-sm">Precio:</span>
              <EstadoPrecio estado={tarifa} />
            </div>
            {/* El aviso de `imprenta` cuando el cliente es VIP y la tarifa no
                tiene precio VIP: se cobra el regular, y conviene que se vea. */}
            {clienteEsVip && tarifa?.estado === "encontrado" && !tarifa.precio.usoPrecioVip ? (
              <p className="mt-1 text-amber-600 text-xs">
                Sin precio VIP definido, se aplica precio regular
              </p>
            ) : null}
          </div>
        ) : (
          <div className="text-neutral-500 text-sm">Seleccione un producto del catalogo</div>
        )}

        <div className="text-right">
          <span className="mr-2 text-neutral-500 text-sm">Subtotal:</span>
          {subtotal === null ? (
            <span className="font-medium text-amber-600 text-sm">Pendiente</span>
          ) : (
            <span className="font-semibold text-brand-600 text-lg">{pesos(subtotal)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * El estado del precio, con las mismas cuatro caras que `imprenta`.
 *
 * «Seleccione tamano y base» no es lo mismo que «Pendiente cotizar»: la primera
 * dice que todavía no se preguntó, la segunda que se preguntó y no hay tarifa.
 */
function EstadoPrecio({ estado }: { estado: EstadoDeTarifa | null }) {
  if (!estado) {
    return <span className="text-neutral-500 text-sm">Seleccione tamano y base</span>;
  }
  if (estado.estado === "buscando") {
    return (
      <span className="flex items-center text-neutral-500 text-sm">
        {/* El spinner es la mitad del mensaje: sin él, «Buscando...» aparece y
            desaparece tan rápido que se lee como un parpadeo raro en vez de
            como el sistema trabajando. */}
        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        Buscando...
      </span>
    );
  }
  if (estado.estado === "encontrado") {
    return (
      <span className="flex items-center font-medium text-green-600 text-sm">
        <Check className="mr-1 h-3 w-3" />
        {pesos(Number(estado.precio.precio))}
      </span>
    );
  }
  if (estado.estado === "error") {
    return <span className="text-red-600 text-sm">No se pudo consultar: {estado.mensaje}</span>;
  }
  return (
    <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700 text-xs">
      <AlertCircle className="mr-1 h-3 w-3" />
      Pendiente cotizar
    </span>
  );
}
